import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { afterSignIn } from './signin.tsx';

const ROLE_LABEL: Record<string, string> = {
  admin: 'an admin', manager: 'a manager', specialist: 'a specialist', viewer: 'a viewer',
  partner_admin: 'a partner admin', partner_user: 'a partner user', client_admin: 'a client admin', client_user: 'a client user',
};

/** Public: someone opens their invitation link and creates their account. */
export function AcceptInvite() {
  const { id } = useParams({ from: '/invite/$id' });
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['invite', id], queryFn: async () => {
    const r = await fetch(`/api/v1/invitations/${id}`);
    return r.ok ? (await r.json() as { email: string; role: string; orgName: string; status: string; existingUser: boolean }) : null;
  } });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function accept(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const r = await fetch(`/api/v1/invitations/${id}/accept`, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name, password }) });
    setBusy(false);
    if (!r.ok) return setError((await r.json().catch(() => ({}))).error ?? 'Something went wrong.');
    return afterSignIn(navigate);
  }

  const v = q.data;
  return (
    <div className="cx-app auth-screen">
      <section className="cx-hero-panel auth-card cx-rise">
        <div className="cx-eyebrow">CAIOS · <b>invitation</b></div>
        {q.isPending && <p className="cx-body">Checking your invitation…</p>}
        {!q.isPending && (!v || v.status !== 'pending') && (<>
          <h1 className="cx-hero">This invitation can’t be used</h1>
          <p className="cx-body">It may have expired, been cancelled, or already been used. Ask the person who invited you for a new link.</p>
        </>)}
        {v && v.status === 'pending' && v.existingUser && (<>
          <h1 className="cx-hero">You already have an account</h1>
          <p className="cx-body">Sign in as {v.email}, then open this link again.</p>
        </>)}
        {v && v.status === 'pending' && !v.existingUser && (<form onSubmit={accept} className="auth-form">
          <h1 className="cx-hero">Join {v.orgName}</h1>
          <p className="cx-body">You’ve been invited as {ROLE_LABEL[v.role] ?? v.role}. Your sign-in is <b>{v.email}</b>.</p>
          <label className="field"><span className="cx-label">Your name</span>
            <input autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name" /></label>
          <label className="field"><span className="cx-label">Choose a password (12+ characters)</span>
            <input type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="accept-password" /></label>
          {error && <p className="auth-error" role="alert">{error}</p>}
          <button type="submit" className="cx-btn cx-btn--primary auth-wide" disabled={busy} data-testid="accept-submit">{busy ? 'Creating your account…' : 'Create my account'}</button>
        </form>)}
      </section>
    </div>
  );
}
